// src/components/MembershipModal.jsx
// Pricing tiers + checkout entrypoint (modal)

import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { rememberPendingCheckout } from '../utils/paymentRecovery';

const mono = 'JetBrains Mono, monospace';
const display = 'Syne, sans-serif';

const TierKicker = ({ label, color }) => (
  <div style={{ fontSize: 9, letterSpacing: '.18em', color, fontFamily: mono, fontWeight: 800 }}>
    {label}
  </div>
);

const Chip = ({ text, color }) => (
  <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 8px',
    borderRadius: 999,
    background: `${color}18`,
    border: `1px solid ${color}33`,
    color,
    fontSize: 9,
    letterSpacing: '.08em',
    fontFamily: mono,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  }}>
    {text}
  </span>
);

const Line = ({ ok, children }) => (
  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
    <div style={{
      width: 18,
      height: 18,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 3,
      border: `1px solid ${ok ? 'var(--green)' : 'var(--red)'}33`,
      background: ok ? 'var(--ggreen)' : 'var(--red)12',
      color: ok ? 'var(--green)' : 'var(--red)',
      fontFamily: mono,
      fontSize: 10,
      flexShrink: 0,
      marginTop: 1,
    }}>
      {ok ? '✓' : '✕'}
    </div>
    <div style={{ fontSize: 11, color: 'var(--t2)', lineHeight: 1.6 }}>
      {children}
    </div>
  </div>
);

export default function MembershipModal({ onClose, onAuthOpen, defaultFocus = null }) {
  const overlayRef = useRef();
  const { user, tier } = useAuth();
  const [billing, setBilling] = useState('quarterly'); // quarterly | yearly
  const [checkoutBusy, setCheckoutBusy] = useState('');
  const [billingStatus, setBillingStatus] = useState(null);
  const focus = defaultFocus;

  const isResearcher = tier === 'annual' || tier === 'researcher' || tier === 'pro';
  const isLifetime = tier === 'lifetime';
  const canManageBilling = !!billingStatus?.customer_portal_url;

  const researcherPrice = billing === 'yearly'
    ? { main: '$199', sub: 'per year', hint: 'Best annual pricing' }
    : { main: '$69', sub: 'per quarter', hint: '4 times per year' };

  const quotas = useMemo(() => ([
    ['Auto EDA', '10 / month'],
    ['Document Analysis', '10 / month'],
    ['Image Insights', '100 / month'],
    ['Speech Studio', '10 / month'],
    ['ML/DL Experiments', '10 / month'],
    ['Video Intelligence', '10 / month'],
  ]), []);

  useEffect(() => {
    // If caller requested focus, nudge default billing to yearly for the "serious" plans.
    if (focus === 'lifetime' || focus === 'annual' || focus === 'researcher') setBilling('yearly');
  }, [focus]);

  useEffect(() => {
    // Prevent background scroll while the modal is open.
    if (typeof document === 'undefined') return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    if (!user) {
      setBillingStatus(null);
      return undefined;
    }

    let cancelled = false;
    api.payments.status()
      .then((data) => {
        if (!cancelled) setBillingStatus(data);
      })
      .catch(() => {
        if (!cancelled) setBillingStatus(null);
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  const needAuth = () => {
    onClose?.();
    onAuthOpen?.('signup');
  };

  const openBillingPortal = () => {
    const url = billingStatus?.customer_portal_url;
    if (url) window.location.assign(url);
  };

  const startCheckout = async (plan) => {
    if (!user) {
      onClose?.();
      onAuthOpen?.('login');
      return;
    }

    setCheckoutBusy(plan);
    try {
      const data = await api.payments.checkout(plan);
      if (data?.checkout_url) {
        rememberPendingCheckout(plan);
        window.location.assign(data.checkout_url);
      }
    } catch (err) {
      if (err?.status === 409 && err?.data?.customer_portal_url) {
        window.location.assign(err.data.customer_portal_url);
        return;
      }
      window.alert(err?.data?.error || err?.message || 'Failed to start checkout');
    } finally {
      setCheckoutBusy('');
    }
  };

  const BillingTabs = ({ accent = 'var(--cyan)' }) => (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      {[
        ['quarterly', 'QUARTERLY'],
        ['yearly', 'YEARLY'],
      ].map(([id, label]) => {
        const active = billing === id;
        return (
          <button
            key={id}
            onClick={() => setBilling(id)}
            style={{
              padding: '5px 9px',
              borderRadius: 999,
              border: `1px solid ${active ? accent : 'var(--b1)'}`,
              background: active ? `${accent}18` : 'transparent',
              color: active ? accent : 'var(--t3)',
              fontFamily: mono,
              fontSize: 8,
              letterSpacing: '.14em',
              fontWeight: 900,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  const tierCard = ({ accent, badge, title, tagline, priceMain, priceSub, priceHint, cta, onCta, sections, headerRight = null, footer = null }) => (
    <div style={{
      background: 'linear-gradient(180deg, rgba(255,255,255,.02), rgba(255,255,255,0))',
      border: `1px solid ${accent}33`,
      borderTop: `3px solid ${accent}`,
      borderRadius: 8,
      overflow: 'hidden',
      position: 'relative',
      boxShadow: '0 18px 55px rgba(0,0,0,.35)',
    }}>
      <div style={{ padding: '18px 18px 14px', background: 'rgba(2,5,8,.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <TierKicker label={badge} color={accent} />
          {headerRight}
        </div>

        <div style={{ marginTop: 10, fontFamily: display, fontWeight: 900, fontSize: 18, color: 'var(--t1)' }}>
          {title}
        </div>
        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--t3)', lineHeight: 1.7 }}>
          {tagline}
        </div>

        <div style={{ marginTop: 12, display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <div style={{ fontFamily: display, fontWeight: 900, fontSize: 34, color: accent, lineHeight: 1 }}>
            {priceMain}
          </div>
          <div style={{ fontSize: 10, color: 'var(--t3)', fontFamily: mono, letterSpacing: '.1em' }}>
            {priceSub}
          </div>
        </div>
        {priceHint && (
          <div style={{ marginTop: 6, fontSize: 10, color: 'var(--t2)', fontFamily: mono, letterSpacing: '.06em' }}>
            {priceHint}
          </div>
        )}

        <button
          onClick={onCta}
          style={{
            width: '100%',
            marginTop: 14,
            padding: '12px 12px',
            background: accent,
            border: 'none',
            borderRadius: 6,
            color: '#020508',
            fontSize: 10,
            fontFamily: mono,
            fontWeight: 900,
            letterSpacing: '.14em',
            cursor: 'pointer',
          }}
        >
          {cta}
        </button>
      </div>

      <div style={{ padding: '14px 18px 18px', background: 'rgba(2,5,8,.25)', borderTop: `1px solid ${accent}22` }}>
        <div style={{ display: 'grid', gap: 14 }}>
          {sections.map((sec) => (
            <div key={sec.title} style={{ border: '1px solid var(--b0)', borderRadius: 6, background: 'rgba(2,5,8,.18)', padding: '10px 10px' }}>
              <div style={{ fontSize: 9, letterSpacing: '.18em', fontFamily: mono, fontWeight: 800, color: 'var(--t3)', marginBottom: 8 }}>
                {sec.title}
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {sec.lines.map((l, idx) => (
                  <Line key={idx} ok={l.ok}>
                    {l.text}
                  </Line>
                ))}
              </div>
            </div>
          ))}
        </div>
        {footer}
      </div>
    </div>
  );

  return (
    <div
      ref={overlayRef}
      onClick={(e) => e.target === overlayRef.current && onClose?.()}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 220,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
        background: 'rgba(2,5,8,.86)',
        backdropFilter: 'blur(6px)',
        overscrollBehavior: 'contain',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 1100,
          maxHeight: '86vh',
          overflow: 'hidden',
          borderRadius: 10,
          border: '1px solid var(--b1)',
          background: 'linear-gradient(180deg, rgba(16,185,129,.08), rgba(2,5,8,.55) 30%, rgba(2,5,8,.92) 100%)',
          animation: 'authSlideUp .25s cubic-bezier(.16,1,.3,1)',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Subtle pattern */}
        <div style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'radial-gradient(800px 420px at 20% 0%, rgba(34,211,238,.10), transparent 60%),' +
            'radial-gradient(700px 380px at 80% 0%, rgba(245,158,11,.10), transparent 60%),' +
            'repeating-linear-gradient(135deg, rgba(255,255,255,.035) 0, rgba(255,255,255,.035) 1px, transparent 1px, transparent 14px)',
          mixBlendMode: 'screen',
          opacity: 0.55,
        }} />

        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--b0)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: display, fontWeight: 900, fontSize: 18, color: 'var(--t1)' }}>
                Pricing
              </div>
              <div style={{ marginTop: 4, fontSize: 11, color: 'var(--t3)', lineHeight: 1.6 }}>
                {user ? `Signed in as ${user.email}` : 'Guest mode'} · Choose the plan that fits your workflow
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                border: '1px solid var(--b1)',
                background: 'rgba(2,5,8,.35)',
                color: 'var(--t3)',
                cursor: 'pointer',
                fontSize: 14,
              }}
              aria-label="Close pricing modal"
            >
              ✕
            </button>
          </div>

          <div style={{ padding: '16px 18px 18px', overflowY: 'auto', minHeight: 0, flex: 1, overscrollBehavior: 'contain' }}>
            <div className="dd-membership-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14 }}>
              {tierCard({
                accent: 'var(--t3)',
                badge: 'JORVEX FREE',
                title: 'Explorer',
                tagline: 'Free forever. Perfect for trying workflows end-to-end.',
                priceMain: '$0',
                priceSub: 'forever',
                priceHint: 'No card required',
                cta: user ? 'KEEP EXPLORING' : 'CREATE FREE ACCOUNT',
                onCta: user ? onClose : needAuth,
                sections: [
                  {
                    title: 'ACCESS (MONTHLY CAPS)',
                    lines: quotas.map(([k, v]) => ({ ok: true, text: <span><span style={{ color: 'var(--t1)', fontWeight: 700 }}>{k}</span> <span style={{ color: 'var(--t3)' }}>·</span> {v}</span> })),
                  },
                  {
                    title: 'NO ACCESS',
                    lines: [
                      { ok: false, text: <span><span style={{ color: 'var(--t1)', fontWeight: 700 }}>Built-in AI Agents</span> (your own agent roster + tools)</span> },
                      { ok: false, text: <span><span style={{ color: 'var(--t1)', fontWeight: 700 }}>Research Memory</span> (cross-session recall + saved context)</span> },
                      { ok: false, text: <span><span style={{ color: 'var(--t1)', fontWeight: 700 }}>Automation Workflows</span> (pipelines + scheduled runs)</span> },
                      { ok: false, text: <span><span style={{ color: 'var(--t1)', fontWeight: 700 }}>Private Discord</span> + community drops</span> },
                    ],
                  },
                ],
              })}

              {tierCard({
                accent: 'var(--cyan)',
                badge: 'JORVEX PRO',
                title: 'Researcher',
                tagline: 'Unlimited analysis, agents, memory, and automation.',
                priceMain: researcherPrice.main,
                priceSub: researcherPrice.sub,
                priceHint: researcherPrice.hint,
                cta: isResearcher ? 'CURRENT PLAN' : (checkoutBusy === (billing === 'yearly' ? 'annual' : 'quarterly') ? 'REDIRECTING...' : (user ? 'UPGRADE TO PRO' : 'SIGN IN TO UPGRADE')),
                onCta: () => {
                  if (!user) { onClose?.(); onAuthOpen?.('login'); return; }
                  if (isResearcher || isLifetime) return;
                  startCheckout(billing === 'yearly' ? 'annual' : 'quarterly');
                },
                headerRight: <BillingTabs accent="var(--cyan)" />,
                sections: [
                  {
                    title: 'UNLIMITED WORKFLOWS',
                    lines: [
                      { ok: true, text: <span><span style={{ color: 'var(--t1)', fontWeight: 700 }}>Unlimited</span> Auto EDA / Doc / Image / Speech / ML / DL / Video runs</span> },
                      { ok: true, text: <span><span style={{ color: 'var(--t1)', fontWeight: 700 }}>Export</span> full reports (MD/PDF where supported)</span> },
                      // { ok: true, text: <span><span style={{ color: 'var(--t1)', fontWeight: 700 }}>Command Line Terminal (CLT) </span> full access</span> },

                    ],
                  },
                  {
                    title: 'AGENTS + MEMORY + AUTOMATION',
                    lines: [
                      { ok: true, text: <span><span style={{ color: 'var(--t1)', fontWeight: 700 }}>Built-in AI Agents</span> (local tiers + orchestration)</span> },
                      { ok: true, text: <span><span style={{ color: 'var(--t1)', fontWeight: 700 }}>Research Memory</span> (saved runs + recall)</span> },
                      { ok: true, text: <span><span style={{ color: 'var(--t1)', fontWeight: 700 }}>Automation workflows</span> (pipeline templates)</span> },
                      { ok: true, text: <span><span style={{ color: 'var(--t1)', fontWeight: 700 }}>Private Discord</span> + community office hours</span> },
                    ],
                  },
                ],
                footer: canManageBilling ? (
                  <button
                    onClick={openBillingPortal}
                    style={{
                      width: '100%',
                      marginTop: 12,
                      padding: '10px 12px',
                      borderRadius: 6,
                      border: '1px solid var(--cyan)33',
                      background: 'var(--cyan)12',
                      color: 'var(--cyan)',
                      fontSize: 10,
                      fontFamily: mono,
                      fontWeight: 800,
                      letterSpacing: '.12em',
                      cursor: 'pointer',
                    }}
                  >
                    MANAGE BILLING
                  </button>
                ) : null,
              })}

              {tierCard({
                accent: 'var(--gold)',
                badge: 'LIFETIME ACCESS',
                title: 'Supporter',
                tagline: 'Everything in Researcher. One-time pay. Lifetime Access.',
                priceMain: '$420',
                priceSub: 'once',
                priceHint: 'No renewals. Ever.',
                cta: isLifetime ? 'LIFETIME ACTIVE' : (checkoutBusy === 'lifetime' ? 'REDIRECTING...' : (user ? 'GET LIFETIME' : 'SIGN IN TO BUY')),
                onCta: () => {
                  if (!user) { onClose?.(); onAuthOpen?.('login'); return; }
                  if (isLifetime) return;
                  startCheckout('lifetime');
                },
                sections: [
                  {
                    title: 'INCLUDED',
                    lines: [
                      { ok: true, text: <span><span style={{ color: 'var(--t1)', fontWeight: 700 }}>Everything in Researcher</span> (unlimited + agents + memory + automation)</span> },
                      { ok: true, text: <span><span style={{ color: 'var(--t1)', fontWeight: 700 }}>Supporter badge</span> + lifetime role</span> },
                      { ok: true, text: <span><span style={{ color: 'var(--t1)', fontWeight: 700 }}>Early access</span> to new labs and drops</span> },
                    ],
                  },
                  {
                    title: 'NOTES',
                    lines: [
                      { ok: true, text: <span>Limited quantity offer. If you want it, grab it.</span> },
                      { ok: true, text: <span>Includes all future premium upgrades.</span> },
                    ],
                  },
                ],
              })}
            </div>

            <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 8, border: '1px solid var(--b0)', background: 'rgba(2,5,8,.30)' }}>
              <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '.16em', color: 'var(--t3)', marginBottom: 6 }}>
                HOW USAGE CAPS WORK
              </div>
              <div style={{ fontSize: 11, color: 'var(--t2)', lineHeight: 1.8 }}>
                Explorer caps reset monthly. A "run" is one completed report (Auto EDA report, Document report, Image report, Speech report, ML experiment save, Video report). Abuse protection applies for automated scraping or mass uploads.
              </div>
            </div>
            {canManageBilling && (
              <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 8, border: '1px solid var(--cyan)22', background: 'rgba(34,211,238,.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '.16em', color: 'var(--cyan)', marginBottom: 6 }}>
                    BILLING PORTAL
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--t2)', lineHeight: 1.8 }}>
                    Cancel recurring billing or update payment details here before changing plans.
                  </div>
                </div>
                <button
                  onClick={openBillingPortal}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 6,
                    border: '1px solid var(--cyan)33',
                    background: 'var(--cyan)12',
                    color: 'var(--cyan)',
                    fontSize: 10,
                    fontFamily: mono,
                    fontWeight: 800,
                    letterSpacing: '.12em',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  OPEN BILLING PORTAL
                </button>
              </div>
            )}
          </div>
        </div>

        <style>{`
          @keyframes authSlideUp {
            from { opacity: 0; transform: translateY(16px) scale(.98); }
            to { opacity: 1; transform: none; }
          }
          @media (max-width: 980px) {
            .dd-membership-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </div>
    </div>
  );
}
